import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, User, MessageSquare } from 'lucide-react';


export const ReviewForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    review: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate submission (in real app, this would save to database)
    setTimeout(() => {
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          rating: 5,
          review: ''
        });
        setIsSubmitted(false);
      }, 3000);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="card p-8 max-w-lg w-full mx-auto text-center"
      >
        <motion.div
          className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Star className="w-10 h-10 text-success fill-current" />
        </motion.div>
        <h3 className="text-2xl font-bold gradient-text mb-4">Thank You for Your Review! ⭐</h3>
        <p className="text-gray-600 mb-6">Your feedback helps us improve and serve you better.</p>
        <div className="text-sm text-gray-500">Form will reset in a moment...</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card p-8 max-w-lg w-full mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-r from-ginger to-ginger-dark rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
          whileHover={{ scale: 1.1, rotate: 10 }}
        >
          <MessageSquare className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-3xl font-bold gradient-text mb-2">Share Your Experience</h2>
        <p className="text-gray-600">Your feedback helps us improve our homemade ginger garlic paste</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <User className="w-4 h-4 mr-2 text-ginger" />
            Your Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
            placeholder="Enter your full name"
          />
        </motion.div>

        {/* Email Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <User className="w-4 h-4 mr-2 text-ginger" />
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
            placeholder="Enter your email address"
          />
        </motion.div>

        {/* Rating Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Rating *
          </label>
          <div className="flex justify-center space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <motion.button
                key={star}
                type="button"
                onClick={() => handleRatingChange(star)}
                className="p-2 transition-all duration-200"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <Star
                  className={`w-8 h-8 transition-colors duration-200 ${
                    star <= formData.rating
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300 hover:text-yellow-200'
                  }`}
                />
              </motion.button>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            {formData.rating === 5 && 'Excellent! ⭐⭐⭐⭐⭐'}
            {formData.rating === 4 && 'Very Good! ⭐⭐⭐⭐'}
            {formData.rating === 3 && 'Good! ⭐⭐⭐'}
            {formData.rating === 2 && 'Fair! ⭐⭐'}
            {formData.rating === 1 && 'Needs Improvement! ⭐'}
          </p>
        </motion.div>

        {/* Review Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2 text-ginger" />
            Your Review *
          </label>
          <textarea
            name="review"
            value={formData.review}
            onChange={handleInputChange}
            rows={4}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm resize-none"
            placeholder="Share your experience with our homemade ginger garlic paste..."
          />
        </motion.div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
          className="w-full btn-primary text-lg shadow-glow-lg flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <motion.div
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <span>Submitting Review...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Submit Review</span>
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};
